import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import loading from "../../assets/images/common/loading.gif";
import { Modal, Row, Col, Form, Button, OverlayTrigger } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import AppDataGrid from "../../components/common/AppDataGrid";

const InventoryCategory = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [filter, setFilter] = useState({
    category_name: "",
    description: "",
    action: "FILTER",
  });

  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(baseUrl + "/inventorycategory/", { 
        action: "READ",
        school_id: userObj.school_id 
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching inventory categories:", error);
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, userObj.school_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditClick = (category) => {
    navigate("/addinventorycategory", {
      state: { categoryData: category },
    });
  };

  const handleDeleteClick = async (category_id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      await axios.post(baseUrl + "/inventorycategory/", {
        category_id,
        action: "DELETE",
        school_id: userObj.school_id,
        lastmodifiedby: userObj.user_id
      });
      toast.success("Category deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    }
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(baseUrl + "/inventorycategory/", {
        ...filter,
        school_id: userObj.school_id
      });
      setCategories(response.data || []);
      setShowFilterModal(false);
    } catch (error) {
      console.error("Error filtering categories:", error);
      toast.error("Failed to filter categories");
    }
  };

  const handleFilterClear = () => {
    setFilter({ category_name: "", description: "", action: "FILTER" });
    fetchData();
  };

  const columns = [
    {
      field: "category_name",
      headerName: "Category",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span style={{ color: params.value === "A" ? "green" : "red", fontWeight: "bold" }}>
          {params.value === "A" ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <div className="tableActions">
          <Tooltip title="Edit" arrow>
            <span className="commonActionIcons" onClick={() => handleEditClick(params.row)}>
              <MdEdit />
            </span>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <span className="commonActionIcons" onClick={() => handleDeleteClick(params.row.category_id)}>
              <MdDelete />
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  const filteredRecords = categories.filter((cat) => {
    const query = searchQuery.toLowerCase();
    return (
      (cat.category_name || "").toLowerCase().includes(query) ||
      (cat.description || "").toLowerCase().includes(query) ||
      (cat.is_active === "A" ? "active" : "inactive").includes(query)
    );
  });

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Inventory Category</h6>
          </div>
          <div className="">
            <input
              type="text"
              placeholder="Search..."
              className="searchInput"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip>Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Add</Tooltip>}>
              <Button className="primaryBtn" variant="primary" onClick={() => navigate("/addinventorycategory")}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(row) => row.category_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Category Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={filter.category_name}
                      onChange={(e) => setFilter({ ...filter, category_name: e.target.value })}
                      placeholder="Enter Category Name"
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={filter.description}
                      onChange={(e) => setFilter({ ...filter, description: e.target.value })}
                      placeholder="Enter Description"
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleFilterClear}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InventoryCategory;
